import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TemperatureBadge, StatusBadge, UserAvatar } from "@/components/crm/Badges";
import { fullName, relDate, fmtDate, fmtEuro, isOverdue, TEMPERATURES, TEMP_ORDER } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import WhatsAppQueue from "@/components/crm/WhatsAppQueue";
import BulkAssignDialog from "@/components/crm/BulkAssignDialog";
import NextActionCell from "@/components/crm/NextActionCell";
import { Label } from "@/components/ui/label";
import { Phone, PhoneOff, Plus, AlertTriangle, Loader2, SlidersHorizontal, X, MessageCircle, Users } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const QUICK_FILTERS = [
  { key: "all", label: "Tutti" },
  { key: "no_next_action", label: "⚠️ Senza prossima azione" },
  { key: "never_contacted", label: "Mai contattati" },
  { key: "deal_open", label: "💼 Trattative aperte" },
  { key: "deal_won", label: "🟢 Vinte" },
  { key: "deal_lost", label: "🔴 Perse" },
  { key: "clients", label: "Clienti" },
  { key: "lost", label: "Persi" },
  { key: "manual", label: "✍️ Inseriti a mano" },
  { key: "invalid_phone", label: "Telefono non valido" },
];

const BUCKET_LABELS = {
  never_contacted: "Mai contattati",
  to_recall: "Da richiamare",
  no_next_action: "Senza prossima azione",
  da_gestire: "Da gestire",
};

const STATE_KEY = "leads_list_state";
const DEFAULT_FILTERS = { status: "", temperature: "", interest: "", assigned_to: "", fonte: "", sorgente: "", campaign: "", has_agent_mediation: "", has_diploma: "", sort: "acq_desc" };

function readSaved(qs) {
  try {
    const s = JSON.parse(sessionStorage.getItem(STATE_KEY) || "null");
    return s && s.qs === qs ? s : null;
  } catch { return null; }
}

export default function Leads() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [saved] = useState(() => readSaved(location.search));
  const [urlInit] = useState(() => {
    const sp = new URLSearchParams(location.search);
    const f = sp.get("am_from"), t = sp.get("am_to");
    return { assigned_to: sp.get("assigned_to") || "", bucket: sp.get("bucket") || "",
      am: f && t ? { from: f, to: t, state: sp.get("am_state") || "" } : null };
  });
  const reqId = useRef(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [leads, setLeads] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [interests, setInterests] = useState([]);
  const [users, setUsers] = useState([]);
  const [facets, setFacets] = useState({ fonti: [], sorgenti: [], campaigns: [] });
  const [quick, setQuick] = useState(saved?.quick || "all");
  const [filters, setFilters] = useState(saved?.filters || { ...DEFAULT_FILTERS, assigned_to: urlInit.assigned_to });
  const [search, setSearch] = useState(saved?.search || "");
  const [showFilters, setShowFilters] = useState(!!saved?.showFilters);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [waOpen, setWaOpen] = useState(false);
  const [bucket, setBucket] = useState(saved ? saved.bucket || "" : urlInit.bucket);
  const [am, setAm] = useState(saved ? saved.am || null : urlInit.am);

  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allVisibleSelected = leads && leads.length > 0 && leads.every((l) => selected.has(l.id));
  const toggleAll = () => setSelected((prev) => {
    if (!leads) return prev;
    if (leads.every((l) => prev.has(l.id))) { const n = new Set(prev); leads.forEach((l) => n.delete(l.id)); return n; }
    const n = new Set(prev); leads.forEach((l) => n.add(l.id)); return n;
  });
  const selectedLeads = (leads || []).filter((l) => selected.has(l.id));

  const load = useCallback(async () => {
    const params = { sort: filters.sort };
    Object.entries(filters).forEach(([k, v]) => { if (v && k !== "sort") params[k] = v; });
    if (quick !== "all") {
      if (quick === "manual") params.sorgente = "Inserimento manuale";
      else params[quick] = true;
    }
    if (search) params.search = search;
    if (bucket) params.bucket = bucket;
    if (am) { params.am_from = am.from; params.am_to = am.to; if (am.state) params.am_state = am.state; }
    const my = ++reqId.current;
    const { data } = await api.get("/leads", { params });
    if (my === reqId.current) setLeads(data);
  }, [filters, quick, search, bucket, am]);

  useEffect(() => { load(); }, [load]);

  // Conserva filtri e posizione di scorrimento per il ritorno con "Indietro"
  const persist = useCallback((scrollY) => {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({ qs: location.search, quick, filters, search, bucket, am, showFilters, scrollY }));
  }, [location.search, quick, filters, search, bucket, am, showFilters]);
  useEffect(() => { persist(window.scrollY); }, [persist]);
  const openLead = (id) => { persist(window.scrollY); navigate(`/leads/${id}`); };
  useEffect(() => {
    api.get("/statuses").then((r) => setStatuses(r.data));
    api.get("/interests").then((r) => setInterests(r.data));
    api.get("/leads/facets").then((r) => setFacets(r.data)).catch(() => {});
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "commerciale")));
  }, [isAdmin]);

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v && k !== "sort").length;

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">Lead</h1>
          <p className="text-sm text-muted-foreground">{leads ? `${leads.length} lead` : "…"}</p>
        </div>
        <NewLeadDialog open={creating} setOpen={setCreating} statuses={statuses} interests={interests} users={users} isAdmin={isAdmin} onCreated={load} />
      </div>

      {am && (
        <div data-testid="am-filter-banner" className="flex items-center gap-2 text-sm rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
          <span className="font-semibold">Monitoraggio assegnazioni:</span>
          <span className="inline-flex items-center rounded-full bg-primary text-primary-foreground px-2.5 py-0.5 text-xs font-medium">
            {({ worked: "Lavorati", never_worked: "Mai lavorati", deal_open: "Trattative aperte", deal_won: "Vinti nel periodo", deal_lost: "Persi nel periodo" })[am.state] || "Assegnati nel periodo"}
          </span>
          {filters.assigned_to && users.find((u) => u.id === filters.assigned_to) && (
            <span className="text-muted-foreground">· {users.find((u) => u.id === filters.assigned_to).name}</span>
          )}
          <button data-testid="clear-am-filter" onClick={() => setAm(null)} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" /> Rimuovi
          </button>
        </div>
      )}

      {bucket && (
        <div data-testid="bucket-filter-banner" className="flex items-center gap-2 text-sm rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
          <span className="font-semibold">Filtro attivo:</span>
          <span className="inline-flex items-center rounded-full bg-primary text-primary-foreground px-2.5 py-0.5 text-xs font-medium">{BUCKET_LABELS[bucket] || bucket}</span>
          {filters.assigned_to && users.find((u) => u.id === filters.assigned_to) && (
            <span className="text-muted-foreground">· {users.find((u) => u.id === filters.assigned_to).name}</span>
          )}
          <button data-testid="clear-bucket-filter" onClick={() => setBucket("")} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" /> Rimuovi
          </button>
        </div>
      )}

      {/* Quick filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {QUICK_FILTERS.map((q) => (
          <button key={q.key} data-testid={`quick-filter-${q.key}`}
            onClick={() => setQuick(q.key)}
            className={cn("px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
              quick === q.key ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-accent border-border")}>
            {q.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtra…" className="h-8 w-40" data-testid="leads-search" />
          <Button variant="outline" size="sm" onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters" className="gap-1">
            <SlidersHorizontal className="w-3.5 h-3.5" /> Filtri {activeFilterCount > 0 && <span className="ml-1 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[10px] grid place-items-center">{activeFilterCount}</span>}
          </Button>
        </div>
      </div>

      {/* Advanced filters */}
      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 p-3 rounded-lg border bg-card">
          <FilterSelect label="Stato" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={statuses.map((s) => s.name)} />
          <FilterSelect label="Temperatura" value={filters.temperature} onChange={(v) => setFilters((f) => ({ ...f, temperature: v }))} options={TEMP_ORDER} labels={TEMP_ORDER.reduce((a, k) => ({ ...a, [k]: TEMPERATURES[k].label }), {})} />
          <FilterSelect label="Interesse" value={filters.interest} onChange={(v) => setFilters((f) => ({ ...f, interest: v }))} options={interests.map((i) => i.name)} />
          <FilterSelect label="Fonte" value={filters.fonte} onChange={(v) => setFilters((f) => ({ ...f, fonte: v }))} options={facets.fonti?.length ? facets.fonti : ["Meta Ads", "Google Ads", "Sito", "Referral", "Manuale"]} />
          <FilterSelect label="Sorgente" value={filters.sorgente} onChange={(v) => setFilters((f) => ({ ...f, sorgente: v }))} options={facets.sorgenti || []} />
          <FilterSelect label="Campagna" value={filters.campaign} onChange={(v) => setFilters((f) => ({ ...f, campaign: v }))} options={facets.campaigns || []} />
          <FilterSelect label="Agente Affari Mediazione" value={filters.has_agent_mediation} onChange={(v) => setFilters((f) => ({ ...f, has_agent_mediation: v }))} options={["Sì", "No", "Non specificato"]} />
          <FilterSelect label="Diploma" value={filters.has_diploma} onChange={(v) => setFilters((f) => ({ ...f, has_diploma: v }))} options={["Sì", "No", "Non specificato"]} />
          {isAdmin && <FilterSelect label="Commerciale" value={filters.assigned_to} onChange={(v) => setFilters((f) => ({ ...f, assigned_to: v }))} options={["unassigned", ...users.map((u) => u.id)]} labels={{ unassigned: "🟣 Da assegnare", ...users.reduce((a, u) => ({ ...a, [u.id]: u.name }), {}) }} />}
          <FilterSelect label="Ordina" value={filters.sort} onChange={(v) => setFilters((f) => ({ ...f, sort: v }))} options={["acq_desc", "acq_asc", "priority", "recent", "oldest", "next_contact", "last_contact", "value_desc", "value_asc"]} labels={{ acq_desc: "Acquisizione: più recenti", acq_asc: "Acquisizione: meno recenti", priority: "Priorità", recent: "Creazione CRM ↓", oldest: "Creazione CRM ↑", next_contact: "Prossimo contatto", last_contact: "Ultimo contatto", value_desc: "Valore ↓", value_asc: "Valore ↑" }} clearable={false} />
          <button onClick={() => { setFilters(DEFAULT_FILTERS); }} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 justify-center"><X className="w-3 h-3" /> Azzera</button>
        </div>
      )}

      {/* Barra azioni selezione */}
      {selected.size > 0 && (
        <div data-testid="selection-bar" className="flex items-center gap-3 flex-wrap p-3 rounded-lg border bg-primary/5 border-primary/30">
          <span className="text-sm font-medium" data-testid="selection-count">{selected.size} selezionati</span>
          <Button size="sm" className="gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => setWaOpen(true)} data-testid="start-wa-queue-btn">
            <MessageCircle className="w-4 h-4" /> Avvia coda WhatsApp
          </Button>
          {isAdmin && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setBulkOpen(true)} data-testid="leads-bulk-assign-btn">
              <Users className="w-4 h-4" /> Assegna a commerciali
            </Button>
          )}
          <button onClick={() => setSelected(new Set())} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 ml-auto" data-testid="clear-selection">
            <X className="w-3 h-3" /> Deseleziona
          </button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border bg-card overflow-hidden">
        {!leads ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Nessun lead trovato con questi filtri.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground text-left">
                  <th className="px-3 py-2.5 w-8"><Checkbox checked={allVisibleSelected} onCheckedChange={toggleAll} data-testid="select-all-leads" aria-label="Seleziona tutti" /></th>
                  <th className="px-4 py-2.5 font-medium">Lead</th>
                  <th className="px-3 py-2.5 font-medium">Temperatura</th>
                  <th className="px-3 py-2.5 font-medium">Stato</th>
                  <th className="px-3 py-2.5 font-medium hidden lg:table-cell">Interesse</th>
                  <th className="px-3 py-2.5 font-medium hidden md:table-cell">Prossima azione</th>
                  <th className="px-3 py-2.5 font-medium text-right">Valore</th>
                  {isAdmin && <th className="px-3 py-2.5 font-medium hidden lg:table-cell">Commerciale</th>}
                  <th className="px-3 py-2.5 font-medium text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`}
                    onClick={() => openLead(l.id)}
                    className="border-b last:border-0 hover:bg-muted/40 cursor-pointer transition-colors group">
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleSel(l.id)} data-testid={`select-lead-${l.id}`} aria-label="Seleziona lead" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-semibold">{fullName(l)}</div>
                          <div className="text-xs text-muted-foreground">{l.company} · {l.province}</div>
                          {l.next_contact_at && <div className="md:hidden mt-0.5"><NextActionCell lead={l} canEdit={isAdmin || l.assigned_to === user?.id} onChanged={load} testid="next-action-edit-m" /></div>}
                        </div>
                        {l.no_next_action && <AlertTriangle className="w-4 h-4 text-amber-500" title="Nessuna prossima azione" />}
                      </div>
                    </td>
                    <td className="px-3 py-3"><TemperatureBadge value={l.temperature} /></td>
                    <td className="px-3 py-3"><StatusBadge value={l.status} /></td>
                    <td className="px-3 py-3 hidden lg:table-cell text-muted-foreground">{(l.interests || []).join(", ") || "—"}</td>
                    <td className="px-3 py-3 hidden md:table-cell">
                      <NextActionCell lead={l} canEdit={isAdmin || l.assigned_to === user?.id} onChanged={load} />
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">
                      {l.deal_value ? (
                        <span className={l.deal_status === "won" ? "text-emerald-600 dark:text-emerald-400" : l.deal_status === "lost" ? "text-red-500 line-through opacity-70" : ""}>
                          {fmtEuro(l.deal_value)}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    {isAdmin && <td className="px-3 py-3 hidden lg:table-cell">{l.assigned_user ? <UserAvatar user={l.assigned_user} size={24} /> : <span className="text-xs text-violet-500 font-medium">Da assegnare</span>}</td>}
                    <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {l.invalid_phone ? (
                        <span title={`Telefono non valido: ${l.phone}`} data-testid={`lead-invalid-phone-${l.id}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/40">
                          <PhoneOff className="w-4 h-4" />
                        </span>
                      ) : l.phone && (
                        <a href={`tel:${l.phone}`} data-testid={`lead-call-${l.id}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 transition-colors">
                          <Phone className="w-4 h-4" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BulkAssignDialog open={bulkOpen} onClose={() => setBulkOpen(false)} leadIds={[...selected]} onDone={() => { setSelected(new Set()); load(); }} />
      <WhatsAppQueue open={waOpen} onClose={() => setWaOpen(false)} leads={selectedLeads} userName={user?.name} onLogged={load} />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, labels = {}, clearable = true }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Tutti" /></SelectTrigger>
        <SelectContent>
          {clearable && <SelectItem value="__all">Tutti</SelectItem>}
          {options.map((o) => <SelectItem key={o} value={o}>{labels[o] || o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function NewLeadDialog({ open, setOpen, statuses, interests, users, isAdmin, onCreated }) {
  const EMPTY = { first_name: "", last_name: "", phone: "", email: "", company: "", city: "", province: "", status: "Da contattare", temperature: "poco_caldo", interests: [], assigned_to: "", note: "", deal_value: "" };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dupMatches, setDupMatches] = useState([]);

  const checkDup = async () => {
    if (!form.phone && !form.email) { setDupMatches([]); return; }
    try {
      const { data } = await api.get("/leads/check-duplicate", { params: { phone: form.phone || undefined, email: form.email || undefined } });
      setDupMatches(data.matches || []);
    } catch { setDupMatches([]); }
  };

  const submit = async () => {
    if (!form.first_name) { toast.error("Il nome è obbligatorio"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      payload.deal_value = form.deal_value === "" ? null : Number(form.deal_value);
      if (!isAdmin || !payload.assigned_to) delete payload.assigned_to;
      await api.post("/leads", payload);
      toast.success("Lead creato");
      setOpen(false);
      setForm(EMPTY);
      setDupMatches([]);
      onCreated();
    } catch (e) { toast.error("Errore nella creazione"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setDupMatches([]); } }}>
      <DialogTrigger asChild>
        <Button data-testid="new-lead-btn" className="gap-1"><Plus className="w-4 h-4" /> Nuovo lead</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Nuovo lead</DialogTitle></DialogHeader>
        {dupMatches.length > 0 && (
          <div data-testid="new-lead-dup-warning" className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-xs dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-200">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Esiste già un lead con questo telefono/email ({dupMatches.length}). Puoi comunque procedere: la stessa persona può avere più richieste distinte.</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome *"><Input data-testid="new-lead-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></Field>
          <Field label="Cognome"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></Field>
          <Field label="Telefono"><Input data-testid="new-lead-phone" value={form.phone} onBlur={checkDup} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Email"><Input data-testid="new-lead-email" value={form.email} onBlur={checkDup} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Città"><Input data-testid="new-lead-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
          <Field label="Provincia"><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} /></Field>
          <Field label="Stato">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="new-lead-status"><SelectValue /></SelectTrigger>
              <SelectContent>{statuses.filter((s) => s.active !== false && s.name !== "Mandare mail").map((s) => <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Temperatura">
            <Select value={form.temperature} onValueChange={(v) => setForm({ ...form, temperature: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TEMP_ORDER.map((k) => <SelectItem key={k} value={k}>{TEMPERATURES[k].label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Argomento / Interesse">
            <Select value={form.interests[0] || ""} onValueChange={(v) => setForm({ ...form, interests: [v] })}>
              <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{interests.map((i) => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Valore trattativa (€)"><Input data-testid="new-lead-value" type="number" value={form.deal_value} onChange={(e) => setForm({ ...form, deal_value: e.target.value })} /></Field>
          {isAdmin && (
            <Field label="Assegna a" full>
              <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger data-testid="new-lead-assignee"><SelectValue placeholder="Da assegnare" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Note" full><Textarea data-testid="new-lead-note" rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button data-testid="new-lead-save" onClick={submit} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crea lead</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return <div className={cn("space-y-1", full && "col-span-2")}><Label className="text-xs">{label}</Label>{children}</div>;
}
